/**
 * Frontend Sentry initialization with PIPA-aware PII scrubbing (issue #63).
 *
 * Companion to V3/backend/sentry_init.py — same conservative posture:
 * Sentry's default browser capture pulls a lot of data we cannot ship
 * to a foreign error tracker without violating PIPA's cross-border
 * disclosure obligations. localStorage carries student tokens
 * (studentToken, devTeacherToken) and the lastPassword cache;
 * URL paths leak session IDs (/student/session/{id}); breadcrumbs
 * occasionally include API request bodies that carry student input.
 *
 * This module centralizes:
 *
 *   1. Whether to init Sentry at all (gated on VITE_SENTRY_DSN being
 *      set at build time — unset = no-op, useful for local dev).
 *   2. The `beforeSend` scrubber that strips PII before transmit, and
 *      the `beforeSendTransaction` twin that runs the same passes plus
 *      a span scrub over every sampled trace (#1572).
 *   3. The `environment` tag (staging vs production) sourced from
 *      VITE_SENTRY_ENVIRONMENT or, if unset, defaulting per build mode.
 *   4. The release tag (git SHA) so errors get attributed to the
 *      deploy they originated from. Cloudflare Pages exposes
 *      CF_PAGES_COMMIT_SHA at build time; we surface it as
 *      __BUILD_BRANCH__ already (vite.config.ts) and pull a similar
 *      pattern here for the SHA.
 *
 * When VITE_SENTRY_DSN is absent, initSentry() returns without
 * importing @sentry/react — the dependency is bundled but the SDK is
 * never instantiated. Local dev, the test suite, and any operational
 * pause of Sentry all work with the env var unset.
 */

// URL prefixes whose breadcrumb capture should be redacted because
// the request body or URL itself contains student-supplied data.
const BREADCRUMB_REDACT_PATH_PREFIXES = [
  '/api/sessions',
  '/api/dev/quickstart',
  '/api/admin',
] as const

// Local-storage keys whose values we never want crossing the border.
// The SDK's default browser integration occasionally captures
// localStorage state in error context; this ensures those keys are
// always-redacted defensively.
const LOCALSTORAGE_REDACT_KEYS = [
  'studentToken',
  'devTeacherToken',
  'devTeacherData',
  'devQuickstartLastPassword',
  'siteAccess',
  'teacherClassCode',
  'studentLoginData',
] as const

// Headers we strip from any captured fetch breadcrumb.
const HEADER_REDACT = new Set([
  'authorization',
  'cookie',
  'x-site-password',
  'x-clerk-auth-token',
  // The whole frontend URL, same as its backend `_HEADER_REDACT` entry.
  'referer',
])

// Span `data` keys whose value is a URL. `url.full` carries the fetch
// URL on an `http.client` span and the host page URL on the segment
// span, where `httpContextIntegration` backfills it.
const SPAN_URL_DATA_KEYS = ['url', 'http.url', 'url.full'] as const

// The query and fragment the SDK splits a URL into, plus the Referer
// that same integration copies onto the segment span — dropped the way
// `query_string` and the `HEADER_REDACT` entry are.
const SPAN_DROP_DATA_KEYS = [
  'http.query',
  'http.fragment',
  'http.request.header.referer',
] as const

interface Breadcrumb {
  category?: string
  message?: string
  data?: unknown
}

interface Span {
  op?: string
  description?: string
  data?: Record<string, unknown>
}

interface SentryEvent {
  transaction?: string
  request?: {
    url?: string
    headers?: Record<string, string>
    data?: unknown
    query_string?: string
  }
  breadcrumbs?: Breadcrumb[]
  spans?: Span[]
  user?: { id?: string; email?: string; ip_address?: string }
  contexts?: Record<string, unknown>
}

function shouldRedactPath(url: string | undefined): boolean {
  if (!url) return false
  return BREADCRUMB_REDACT_PATH_PREFIXES.some((prefix) =>
    url.includes(prefix),
  )
}

/**
 * A URL or path with every student-identifying segment templated out.
 *
 * Three tokens cross in a path: the `sess_…` slug on
 * `/student/session/:sessionId` and `/api/sessions/:slug/…`, the class
 * join code on `/teacher/class/:classCode`, and any all-numeric segment
 * (student PKs, module ids). The origin and query are not this helper's
 * business — it returns the path alone, so a caller that needs the host
 * keeps it and a caller that wants a bare route label (the #1107 timeout
 * beacon's `route` tag) can use the return value as is.
 */
export function templateRoutePath(url: string): string {
  let path = url
  try {
    // Resolve against a dummy origin so the pathname extraction works
    // for relative and absolute inputs alike; the host is discarded.
    path = new URL(url, 'http://x').pathname
  } catch {
    // Leave `path` as the raw string — still templated below.
  }
  return path
    .replace(/\/sess_[A-Za-z0-9_-]+/g, '/{slug}')
    .replace(/\/classes\/[^/]+/g, '/classes/{code}')
    .replace(/\/class\/[^/]+/g, '/class/{code}')
    .replace(/\/\d+(?=\/|$)/g, '/{id}')
}

/**
 * `templateRoutePath` applied in place, keeping scheme and host.
 *
 * The query and fragment go with it, for the reason `scrubEvent` drops
 * `query_string`: they carry tokens and class codes (#1572).
 */
function templateUrl(url: string): string {
  const path = templateRoutePath(url)
  try {
    const parsed = new URL(url, 'http://x')
    return url.includes('://') ? `${parsed.origin}${path}` : path
  } catch {
    return path
  }
}

function scrubHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!headers) return headers
  const cleaned: Record<string, string> = {}
  for (const [k, v] of Object.entries(headers)) {
    cleaned[k] = HEADER_REDACT.has(k.toLowerCase()) ? '[redacted]' : v
  }
  return cleaned
}

function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  // fetch-style breadcrumbs Sentry generates from intercepted XHR /
  // fetch calls land here. The crumb.data carries `url`, `method`,
  // `status_code`, sometimes `request_body_size`. We strip the body
  // and redact the URL on PII-bearing paths so the structural
  // crumb (route + method + status) is preserved without payload.
  if (crumb.data && typeof crumb.data === 'object') {
    const data = crumb.data as { url?: string; body?: unknown }
    if (shouldRedactPath(data.url)) {
      crumb.data = { ...data, body: '[redacted]' }
    }
    // Session slugs never cross the border, even inside crumb URLs: the
    // SDK's default fetch-breadcrumb integration records every API call's
    // full URL, so without this a `/api/sessions/sess_.../message` crumb
    // would attach the raw slug to ANY later event — including the #1107
    // `client_request_timeout` beacon, whose tag-level slug scrubbing in
    // `api/client.ts` would otherwise be defeated by its own breadcrumb
    // trail. Replace the identifying segments, keep the structural route.
    if (typeof data.url === 'string') {
      crumb.data = {
        ...(crumb.data as Record<string, unknown>),
        url: templateUrl(data.url),
      }
    }
  }
  // Long messages get truncated — we occasionally log debug info that
  // contains transient tokens; truncating to 100 chars keeps the
  // structural shape (which file logged this, what level, what
  // category) while dropping the tail that's most likely to leak.
  if (typeof crumb.message === 'string' && crumb.message.length > 100) {
    crumb.message = crumb.message.slice(0, 100) + '…[truncated]'
  }
  return crumb
}

function scrubEvent(event: SentryEvent): SentryEvent | null {
  // Request block: strip auth headers + redact body/query on PII paths.
  if (event.request) {
    event.request.headers = scrubHeaders(event.request.headers)
    if (shouldRedactPath(event.request.url) && 'data' in event.request) {
      event.request.data = '[redacted]'
    }
    if (typeof event.request.url === 'string') {
      event.request.url = templateUrl(event.request.url)
    }
    delete event.request.query_string
  }

  // The transaction NAME: `browserTracingIntegration` takes it from
  // `location.pathname`, which on `/student/session/:sessionId` is the
  // slug (#1572). Templated rather than dropped, so grouping survives.
  if (typeof event.transaction === 'string') {
    event.transaction = templateRoutePath(event.transaction)
  }

  // Breadcrumbs: scrub each.
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb)
  }

  // User block: replace with coarse "is anything set" boolean.
  if (event.user) {
    event.user = event.user.id ? { id: '[redacted]' } : {}
  }

  // Contexts can carry localStorage snapshots in Sentry's "browser"
  // context. Walk and redact our sensitive keys defensively.
  if (event.contexts && typeof event.contexts === 'object') {
    for (const ctx of Object.values(event.contexts)) {
      if (ctx && typeof ctx === 'object') {
        const c = ctx as Record<string, unknown>
        for (const key of LOCALSTORAGE_REDACT_KEYS) {
          if (key in c) c[key] = '[redacted]'
        }
      }
    }
    // `contexts.trace` is the segment span, not a context: it is where
    // `httpContextIntegration` backfills the host page URL, and where a
    // `setAttribute` on the active span lands. Same rule as `spans[]`.
    const trace = event.contexts.trace
    if (trace && typeof trace === 'object') {
      event.contexts.trace = scrubSpan(trace as Span)
    }
  }

  return event
}

/**
 * The span rule: `spans[]` on a transaction, `contexts.trace` on both.
 *
 * `browserTracingIntegration`'s fetch/xhr instrumentation records the
 * request URL three times — the `description` plus `http.url` and
 * `url.full` in `data` — and splits the query and fragment into their
 * own entries. On the segment span `httpContextIntegration` backfills
 * `url.full` and the Referer with the host page URL (#1572).
 */
function scrubSpan(span: Span): Span {
  if (typeof span.description === 'string') {
    span.description = span.description.replace(
      /(^|\s)(\/[^\s]*|https?:\/\/[^\s]+)/g,
      (_match, lead: string, url: string) => lead + templateUrl(url),
    )
  }
  if (span.data && typeof span.data === 'object') {
    const data = { ...span.data }
    for (const key of SPAN_URL_DATA_KEYS) {
      if (typeof data[key] === 'string') data[key] = templateUrl(data[key])
    }
    for (const key of SPAN_DROP_DATA_KEYS) {
      if (key in data) data[key] = '[redacted]'
    }
    span.data = data
  }
  return span
}

/**
 * `beforeSendTransaction` — `scrubEvent` plus the `spans[]` pass.
 *
 * `@sentry/react` skips `beforeSend` for a transaction envelope, so a
 * sampled trace reaches none of those passes unless this hook is wired.
 * `contexts.trace` is handled inside `scrubEvent`, because an error
 * event carries that span too (#1572).
 */
function scrubTransaction(event: SentryEvent): SentryEvent | null {
  const scrubbed = scrubEvent(event)
  if (!scrubbed) return null
  if (Array.isArray(scrubbed.spans)) {
    scrubbed.spans = scrubbed.spans.map(scrubSpan)
  }
  return scrubbed
}

export async function initSentry(): Promise<boolean> {
  const dsn = (import.meta.env.VITE_SENTRY_DSN ?? '').trim()
  if (!dsn) return false

  // Lazy-import so the SDK only loads when actually configured. Saves
  // bundle size in dev runs that don't have a DSN set, and keeps the
  // unit tests fast (vitest doesn't pull Sentry into jsdom every
  // file).
  const Sentry = await import('@sentry/react')

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    (import.meta.env.PROD ? 'production' : 'development')

  // Same release-tagging strategy as the backend: if the deploy set
  // VITE_SENTRY_RELEASE explicitly, use that; otherwise fall back to
  // the build branch (vite.config.ts injects __BUILD_BRANCH__) so at
  // least which branch built the bundle is visible.
  const release =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ||
    (typeof __BUILD_BRANCH__ === 'string' ? __BUILD_BRANCH__ : undefined)

  const tracesSampleRate = parseFloat(
    (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string) || '0.1',
  )

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      // Sentry's BrowserTracing used to be implicit — explicit now.
      // Catches navigation + fetch performance. Sample-rate gated.
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: Number.isFinite(tracesSampleRate)
      ? tracesSampleRate
      : 0.1,
    sendDefaultPii: false,
    beforeSend: (event) => scrubEvent(event as SentryEvent) as never,
    // Error events only — the SDK routes a sampled trace to the
    // transaction hook instead, where the span pass also runs (#1572).
    beforeSendTransaction: (event) =>
      scrubTransaction(event as SentryEvent) as never,
  })

  // Expose the loaded SDK on ``window.Sentry`` so deferred, non-React
  // capture sites can reach it WITHOUT statically importing
  // ``@sentry/react`` (which would pull the SDK into their eager bundle
  // and regress the boot-perf this lazy-init pattern protects). The npm
  // ``@sentry/react`` package — unlike the CDN loader bundle — does not
  // self-attach this global, so we set it explicitly. Two consumers rely
  // on it: ``RouteErrorBoundary`` (``'Sentry' in window``) and the
  // client-side timeout beacon in ``src/api/client.ts`` (#1107). Both
  // guard on the property existing and swallow capture failures, so the
  // global is a best-effort convenience, never a hard dependency.
  try {
    ;(window as unknown as { Sentry: unknown }).Sentry = Sentry
  } catch {
    // ``window`` unavailable (SSR / hardened sandbox) — capture sites
    // fall back to console-only, same as when Sentry isn't configured.
  }

  return true
}
